import { useContext, useEffect, useState } from "react";
import { getMenuItems, reorderItems } from "../../../API/Portal/Portal";
import MenuItemTreeView from "./MenuItems/MenuItemTreeView/MenuItemTreeView";
import { Splitter } from "@progress/kendo-react-layout";
import CreateMenuItemDialog from "./CreateMenuItemDialog/CreateMenuItemDialog";
import EditItemForm from "./EditForms/EditItemForm";
import DeleteMenuItemDialog from "./DeleteMenuItemDialog/DeleteMenuItemDialog";
import { removeTreeViewItem } from "./MenuItems/MenuItemTreeView/utils";
import { TreeViewDragAnalyzer, moveTreeViewItem } from "@progress/kendo-react-treeview";
import { useTranslation } from "react-i18next";
import { GlobalContext } from "../../../Renderers/GlobalContext";

function MenuItems({ portalId, projectId }) {

    const [menuItems, setMenuItems] = useState([]);
    const { organization } = useContext(GlobalContext).state
    const [selectedItemIndex, setSelectedItemIndex] = useState("0");
    const [showCreateMenuItemDialog, setShowCreateMenuItemDialog] = useState(false);
    const [showDeleteMenuItemDialog, setShowDeleteMenuItemDialog] = useState(false);
    const { t, i18n } = useTranslation([
        "portal/menu_items"
    ], {
        useSuspense: false
    })

    useEffect(() => {
        // i18n.changeLanguage(organization.platformLanguage)
        getData();
    }, [])

    async function getData() {
        const { data } = await getMenuItems(portalId);
        const menuItems = data.map(item => ({
            ...item,
            expanded: true
        }))
        setMenuItems(menuItems);
    }

    function toggleCreateMenuItemDialog() {
        setShowCreateMenuItemDialog(prevState => !prevState);
    }

    function toggleDeleteMenuItemDialog() {
        setShowDeleteMenuItemDialog(prevState => !prevState);
    }

    function toggleExpand(event) {
        setMenuItems(prevState => prevState.map((item, index) => {
            if (String(index) === event.itemHierarchicalIndex) {
                return {
                    ...item,
                    expanded: !item.expanded
                }
            }
            return item;
        }));
    }

    function addItemToMenuItems(item) {
        setMenuItems(prevState => [...prevState, item]);
        toggleCreateMenuItemDialog();
    }

    function handleItemSelect(event) {
        setSelectedItemIndex(event.itemHierarchicalIndex);
    }

    function handleDelete() {
        toggleDeleteMenuItemDialog();
        const newTree = removeTreeViewItem(menuItems, selectedItemIndex);
        let newSelectedItem = selectedItemIndex;
        if (newTree.length === 0) {
            newSelectedItem = "";
        } else if (newTree.length === 1) {
            newSelectedItem = "0";
        } else {
            const [parentIndex, childIndex] = selectedItemIndex.split("_");
            if (childIndex) {
                const parentItem = menuItems[parentIndex];
                const children = parentItem.children;
                if (children.length === 0) {
                    newSelectedItem = parentIndex;
                } else if (children.length === 1) {
                    newSelectedItem = `${parentIndex}_0`;
                }
            }
        }
        setMenuItems(newTree);
        setSelectedItemIndex(newSelectedItem);
    }

    function handleDragEnd(event) {
        const eventAnalyzer = new TreeViewDragAnalyzer(event).init();
        const direction = eventAnalyzer.getDropOperation();

        if (eventAnalyzer.isDropAllowed && direction !== "child") {
            const newTree = moveTreeViewItem(
                event.itemHierarchicalIndex,
                menuItems,
                direction,
                eventAnalyzer.destinationMeta.itemHierarchicalIndex,
                null,
                "children"
            );
            newTree.map((item, index) => {
                item.order = (index + 1) * 100000000;
                if (item.children) {
                    item.children.map((child, childIndex) => {
                        child.order = (childIndex + 1) * 100000000;
                    })
                }
            })
            reorderItems(portalId, { items: newTree });
            setMenuItems(newTree);
        }
    }

    function updateMenuItem(parentId, itemId, newProperties) {
        if (parentId === portalId) {
            setMenuItems(items => items.map(item => {
                if (item.id === itemId) {
                    return {
                        ...item,
                        ...newProperties
                    }
                }
                return item;
            }))
        } else {
            setMenuItems(items =>
                items.map(item => ({
                    ...item,
                    children: item.children ? item.children.map(child => {
                        if (child.id === itemId) {
                            return {
                                ...child,
                                ...newProperties
                            }
                        }
                        return child;
                    }) : []
                })
            ))
        }
    }

    return (
        <Splitter
            panes={[
                { size: "25%" },
            ]}
        >
            <div>
                {
                    showCreateMenuItemDialog &&
                    <CreateMenuItemDialog
                        menuItems={menuItems}
                        portalId={portalId}
                        projectId={projectId}
                        onToggle={toggleCreateMenuItemDialog}
                        onCreate={addItemToMenuItems}
                    />
                }
                {
                    showDeleteMenuItemDialog &&
                    <DeleteMenuItemDialog
                        portalId={portalId}
                        menuItems={menuItems}
                        selectedItemIndex={selectedItemIndex}
                        toggleDialog={toggleDeleteMenuItemDialog}
                        onDelete={handleDelete} />
                }
                <div className="d-flex section-header-box align-items-center">
                    <div className="bigger flex-grow-1">
                        {t("header.title")}
                    </div>
                    <button
                        title="New Menu Item"
                        className="btn btn-primary"
                        onClick={toggleCreateMenuItemDialog}
                    >
                        <i className="fas fa-plus mx-1"></i> {t("header.buttons.add_menu_item_button_label")}
                    </button>
                </div>
                <MenuItemTreeView
                    data={menuItems}
                    textField="name"
                    childrenField="children"
                    expandIcons={true}
                    expandField={"expanded"}
                    draggable={true}
                    onItemClick={handleItemSelect}
                    onItemDragEnd={handleDragEnd}
                    onExpandChange={toggleExpand}
                />
            </div>
            <div>
                {
                    menuItems.length > 0 &&
                    <EditItemForm
                        projectId={projectId}
                        portalId={portalId}
                        menuItems={menuItems}
                        selectedItemIndex={selectedItemIndex}
                        onDelete={toggleDeleteMenuItemDialog}
                        updateMenuItem={updateMenuItem} />
                }
            </div>
        </Splitter>
    )
}

export default MenuItems;
